import React, { useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import {
  ShoppingBag, Package, Receipt, Plus, Pencil, Trash2, X, RefreshCw,
  DollarSign, TrendingUp, Link2, CheckCircle2, ExternalLink, Copy,
  CreditCard, User, Boxes, Tag,
} from 'lucide-react'
import { getSession } from '../lib/session'
import AgentAsk from '../components/AgentAsk'

// Commerce — a clean console over the tenant's product catalog and order book.
// Products: list + add/edit (name, price, description) against the real CRUD
// endpoints. Orders: status pills, totals, customer, a 'Create payment link'
// action that mints a real Stripe (or manual) link, and 'Mark paid' on pending
// orders. KPIs (orders, revenue, AOV) are derived from the order list.
//
// VISUAL LAYER: the flat "Nunito / Intelligence Briefings" aesthetic — soft
// palette, rounded cards, pills — ported from GrowthLab and scoped under
// `.cm-root` so nothing leaks. Auth rides the global axios Bearer interceptor.
const API = '/api/v1/commerce'

// ---- scoped stylesheet (prefixed .cm-* so nothing leaks) ------------------
const CM_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap');
.cm-root{
  --cm-bg:#FBFBF7; --cm-panel:#FFFFFF; --cm-panel2:#F6F6F0; --cm-line:#ECEBE3;
  --cm-ink:#2E3A45; --cm-mut:#7C8794; --cm-dim:#AEB6BD;
  --cm-accent:#4E9AC4; --cm-up:#4FAE5A; --cm-down:#E24A3B; --cm-warn:#EAB43C; --cm-purple:#8E7CC3;
  --cm-shadow:0 1px 2px rgba(46,58,69,.05),0 10px 26px rgba(46,58,69,.07);
  --cm-shadow-lg:0 10px 24px rgba(46,58,69,.10),0 34px 66px rgba(46,58,69,.15);
  font-family:Nunito,system-ui,-apple-system,Segoe UI,sans-serif;
  color:var(--cm-ink); -webkit-font-smoothing:antialiased;
}
[data-theme="dark"] .cm-root{
  --cm-bg:#0E1116; --cm-panel:#171B22; --cm-panel2:#12161C; --cm-line:#262C36;
  --cm-ink:#E7ECF1; --cm-mut:#95A0AD; --cm-dim:#5C6672;
  --cm-accent:#5FB0DB; --cm-up:#5CC47C; --cm-down:#F0575C; --cm-warn:#EAB43C; --cm-purple:#A796D6;
  --cm-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
  --cm-shadow-lg:0 10px 24px rgba(0,0,0,.45),0 34px 70px rgba(0,0,0,.6);
}
@media (prefers-color-scheme:dark){:root:not([data-theme="light"]) .cm-root{
  --cm-bg:#0E1116; --cm-panel:#171B22; --cm-panel2:#12161C; --cm-line:#262C36;
  --cm-ink:#E7ECF1; --cm-mut:#95A0AD; --cm-dim:#5C6672;
  --cm-accent:#5FB0DB; --cm-up:#5CC47C; --cm-down:#F0575C; --cm-warn:#EAB43C; --cm-purple:#A796D6;
  --cm-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
  --cm-shadow-lg:0 10px 24px rgba(0,0,0,.45),0 34px 70px rgba(0,0,0,.6);
}}
.cm-root *{box-sizing:border-box;}
.cm-title{font-size:24px;font-weight:800;letter-spacing:-.02em;margin:0;display:flex;align-items:center;gap:10px;color:var(--cm-ink);}
.cm-sub{margin:4px 0 0;color:var(--cm-mut);font-size:14px;font-weight:500;line-height:1.5;}
.cm-card{background:var(--cm-panel);border:1px solid var(--cm-line);border-radius:20px;box-shadow:var(--cm-shadow);padding:20px 22px;}
.cm-card.tight{padding:16px 18px;}
.cm-soft{background:var(--cm-panel2);border:1px solid var(--cm-line);border-radius:14px;}
.cm-sec-h{font-size:13px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;color:var(--cm-mut);display:flex;align-items:center;gap:8px;margin:0;}
.cm-elab{font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:var(--cm-mut);margin:0;display:flex;align-items:center;gap:6px;}
.cm-narr{font-size:15px;font-weight:700;line-height:1.5;color:var(--cm-ink);margin:0;}
.cm-body{font-size:14px;font-weight:600;line-height:1.55;color:var(--cm-ink);margin:0;}
.cm-muted{color:var(--cm-mut);font-weight:600;}
.cm-dim{color:var(--cm-dim);font-weight:600;}
.cm-tabnum{font-variant-numeric:tabular-nums;}
.cm-pill{font-size:11px;font-weight:800;padding:4px 11px;border-radius:20px;display:inline-flex;align-items:center;gap:5px;text-transform:capitalize;}
.cm-chip{font-size:11px;font-weight:700;padding:4px 10px;border-radius:20px;background:var(--cm-panel2);border:1px solid var(--cm-line);color:var(--cm-mut);display:inline-flex;align-items:center;gap:5px;white-space:nowrap;}
.cm-icobox{flex:none;width:38px;height:38px;border-radius:11px;display:flex;align-items:center;justify-content:center;}
/* KPI */
.cm-kpi{background:var(--cm-panel);border:1px solid var(--cm-line);border-radius:18px;box-shadow:var(--cm-shadow);padding:16px 18px;display:flex;align-items:center;gap:14px;}
.cm-kpi-v{font-size:26px;font-weight:800;letter-spacing:-.02em;color:var(--cm-ink);line-height:1;}
.cm-kpi-l{font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:var(--cm-mut);margin-top:6px;}
/* buttons */
.cm-btn{font-family:Nunito,system-ui,sans-serif;font-size:13px;font-weight:800;border-radius:12px;padding:9px 15px;cursor:pointer;display:inline-flex;align-items:center;gap:7px;border:1px solid var(--cm-line);background:var(--cm-panel);color:var(--cm-ink);transition:.16s;box-shadow:var(--cm-shadow);}
.cm-btn:hover{transform:translateY(-1px);}
.cm-btn:disabled{opacity:.6;cursor:default;transform:none;}
.cm-btn.sm{font-size:12px;padding:7px 13px;border-radius:10px;}
.cm-btn.primary{background:var(--cm-accent);color:#fff;border-color:transparent;}
.cm-btn.ghost{background:none;border:none;box-shadow:none;color:var(--cm-mut);padding:7px 9px;font-weight:700;}
.cm-btn.ghost:hover{color:var(--cm-ink);}
.cm-btn.link{background:none;border:none;box-shadow:none;padding:0;color:var(--cm-accent);font-size:12.5px;font-weight:800;}
.cm-btn.link:hover{transform:none;filter:brightness(1.08);}
.cm-btn.danger{background:none;border:none;box-shadow:none;color:var(--cm-down);padding:7px 10px;}
.cm-btn.danger:hover{transform:none;filter:brightness(1.05);}
/* form */
.cm-label{font-size:12px;font-weight:800;color:var(--cm-mut);text-transform:uppercase;letter-spacing:.04em;margin-bottom:6px;display:block;}
.cm-input,.cm-textarea{width:100%;font-family:Nunito,system-ui,sans-serif;font-size:14px;font-weight:600;color:var(--cm-ink);background:var(--cm-panel2);border:1px solid var(--cm-line);border-radius:12px;padding:10px 12px;outline:none;transition:.16s;}
.cm-input:focus,.cm-textarea:focus{border-color:var(--cm-accent);}
.cm-textarea{resize:vertical;min-height:74px;line-height:1.5;}
/* modal */
.cm-overlay{position:fixed;inset:0;background:rgba(20,24,30,.5);backdrop-filter:blur(2px);z-index:60;display:flex;align-items:flex-start;justify-content:center;padding:6vh 16px;overflow-y:auto;}
.cm-modal{background:var(--cm-panel);border:1px solid var(--cm-line);border-radius:20px;box-shadow:var(--cm-shadow-lg);width:100%;max-width:460px;padding:22px 24px;}
.cm-spin{animation:cm-spin 1s linear infinite;}
@keyframes cm-spin{to{transform:rotate(360deg);}}
.cm-fade{animation:cm-fade .35s ease both;}
@keyframes cm-fade{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:none;}}
`

let _cmStyleInjected = false
function CMStyle() {
  useEffect(() => {
    if (_cmStyleInjected) return
    const el = document.createElement('style')
    el.setAttribute('data-cm-style', '1')
    el.textContent = CM_CSS
    document.head.appendChild(el)
    _cmStyleInjected = true
  }, [])
  return null
}

// ---- helpers --------------------------------------------------------------
const TONE = {
  up:   { c: 'var(--cm-up)',     bg: 'rgba(79,174,90,.15)' },
  down: { c: 'var(--cm-down)',   bg: 'rgba(226,74,59,.14)' },
  warn: { c: 'var(--cm-warn)',   bg: 'rgba(234,180,60,.16)' },
  acc:  { c: 'var(--cm-accent)', bg: 'rgba(78,154,196,.14)' },
  pur:  { c: 'var(--cm-purple)', bg: 'rgba(142,124,195,.16)' },
  mut:  { c: 'var(--cm-mut)',    bg: 'rgba(124,135,148,.14)' },
}
const pill = (t) => ({ background: t.bg, color: t.c })

// Order status → tone + label.
const STATUS = {
  draft:     { t: TONE.mut,  label: 'Draft' },
  pending:   { t: TONE.warn, label: 'Pending' },
  paid:      { t: TONE.up,   label: 'Paid' },
  fulfilled: { t: TONE.acc,  label: 'Fulfilled' },
  canceled:  { t: TONE.down, label: 'Canceled' },
  cancelled: { t: TONE.down, label: 'Cancelled' },
}
const statusOf = (s) => STATUS[String(s || 'draft').toLowerCase()] || STATUS.draft

const money = (cents, currency = 'USD') => {
  const n = Number(cents || 0) / 100
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: String(currency || 'USD').toUpperCase() }).format(n)
  } catch {
    return `${(currency || 'USD')} ${n.toFixed(2)}`
  }
}
const fmtDate = (d) => {
  if (!d) return ''
  const t = new Date(d)
  if (Number.isNaN(t.getTime())) return ''
  return t.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
const shortId = (id) => (id ? `#${String(id).slice(0, 8)}` : '#—')

export default function Commerce() {
  const session = getSession()
  const [products, setProducts] = useState([])
  const [orders, setOrders] = useState([])
  const [loadingP, setLoadingP] = useState(false)
  const [loadingO, setLoadingO] = useState(false)
  const [editing, setEditing] = useState(null) // null | {} (new) | product (edit)

  const connected = !!session?.namespaceId

  const loadProducts = () => {
    setLoadingP(true)
    axios.get(`${API}/products`, { params: { all: 'true' } })
      .then(r => setProducts(Array.isArray(r.data?.products) ? r.data.products : []))
      .catch(() => setProducts([]))
      .finally(() => setLoadingP(false))
  }
  const loadOrders = () => {
    setLoadingO(true)
    axios.get(`${API}/orders`)
      .then(r => setOrders(Array.isArray(r.data?.orders) ? r.data.orders : []))
      .catch(() => setOrders([]))
      .finally(() => setLoadingO(false))
  }
  useEffect(() => {
    if (!connected) return
    loadProducts()
    loadOrders()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // KPIs derived from the order list.
  const kpis = useMemo(() => {
    const currency = orders[0]?.currency || products[0]?.currency || 'USD'
    const paid = orders.filter(o => ['paid', 'fulfilled'].includes(String(o.status || '').toLowerCase()))
    const revenue = paid.reduce((s, o) => s + Number(o.total_cents || 0), 0)
    const aov = paid.length ? Math.round(revenue / paid.length) : 0
    return { orders: orders.length, revenue, aov, currency, paidCount: paid.length }
  }, [orders, products])

  return (
    <div className="cm-root space-y-8">
      <CMStyle />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="cm-title">
            <ShoppingBag size={24} style={{ color: 'var(--cm-accent)' }} /> Commerce
          </h2>
          <p className="cm-sub">
            Your product catalog and order book — mint payment links, settle orders, sell in chat.
          </p>
        </div>
        {connected && (
          <button onClick={() => setEditing({})} className="cm-btn primary">
            <Plus size={15} /> Add product
          </button>
        )}
      </div>

      <div style={{ maxWidth: '42rem' }}><AgentAsk service="commerce" agentName="Commerce Agent" starters={['How many orders and how much revenue?', 'What is selling best?']} /></div>

      {!connected && (
        <div className="cm-card" style={{ textAlign: 'center', padding: '48px 22px' }}>
          <ShoppingBag size={32} style={{ color: 'var(--cm-dim)', margin: '0 auto 12px' }} />
          <p className="cm-muted" style={{ margin: 0 }}>Connect a workspace to open the commerce console.</p>
        </div>
      )}

      {connected && (
        <>
          {/* KPIs */}
          <div className="grid sm:grid-cols-3 gap-4">
            <Kpi icon={Receipt} tone={TONE.acc} value={kpis.orders.toLocaleString()} label="Orders" />
            <Kpi icon={DollarSign} tone={TONE.up} value={money(kpis.revenue, kpis.currency)} label={`Revenue · ${kpis.paidCount} paid`} />
            <Kpi icon={TrendingUp} tone={TONE.pur} value={money(kpis.aov, kpis.currency)} label="Avg. order value" />
          </div>

          <ProductsSection
            products={products}
            loading={loadingP}
            onRefresh={loadProducts}
            onEdit={(p) => setEditing(p)}
            onNew={() => setEditing({})}
          />

          <OrdersSection
            orders={orders}
            loading={loadingO}
            onRefresh={loadOrders}
          />
        </>
      )}

      {editing !== null && (
        <ProductForm
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadProducts() }}
        />
      )}
    </div>
  )
}

// ---- KPI card -------------------------------------------------------------
function Kpi({ icon: Icon, tone, value, label }) {
  return (
    <div className="cm-kpi cm-fade">
      <span className="cm-icobox" style={pill(tone)}><Icon size={18} /></span>
      <div>
        <div className="cm-kpi-v cm-tabnum">{value}</div>
        <div className="cm-kpi-l">{label}</div>
      </div>
    </div>
  )
}

// ---- PRODUCTS -------------------------------------------------------------
function ProductsSection({ products, loading, onRefresh, onEdit, onNew }) {
  const [deletingId, setDeletingId] = useState('')

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return
    setDeletingId(p.id)
    try {
      await axios.delete(`${API}/products/${p.id}`)
      toast.success('Product deleted')
      onRefresh()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not delete product')
    } finally { setDeletingId('') }
  }

  return (
    <div>
      <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
        <h3 className="cm-sec-h"><Package size={14} /> Products</h3>
        <button onClick={onRefresh} disabled={loading} className="cm-btn ghost sm">
          <RefreshCw size={13} className={loading ? 'cm-spin' : ''} /> Refresh
        </button>
      </div>

      {loading && products.length === 0 && (
        <div className="cm-card cm-muted" style={{ textAlign: 'center', padding: '32px 22px' }}>Loading catalog…</div>
      )}
      {!loading && products.length === 0 && (
        <div className="cm-card" style={{ textAlign: 'center', padding: '40px 22px' }}>
          <Boxes size={28} style={{ color: 'var(--cm-dim)', margin: '0 auto 8px' }} />
          <p className="cm-muted" style={{ fontSize: 13.5, margin: '0 0 14px' }}>No products yet. Add your first item to start selling.</p>
          <button onClick={onNew} className="cm-btn primary sm" style={{ margin: '0 auto' }}><Plus size={13} /> Add product</button>
        </div>
      )}

      <div className="space-y-3">
        {products.map(p => (
          <div key={p.id} className="cm-card tight cm-fade">
            <div className="flex items-start justify-between gap-3">
              <div className="flex gap-3 min-w-0">
                <span className="cm-icobox" style={{ ...pill(TONE.acc), marginTop: 1 }}><Package size={17} /></span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="cm-narr" style={{ fontSize: 15 }}>{p.name || 'Untitled product'}</p>
                    {p.active === false && <span className="cm-pill" style={pill(TONE.mut)}>Inactive</span>}
                  </div>
                  {p.description && <p className="cm-muted" style={{ fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>{p.description}</p>}
                  <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 8 }}>
                    {p.sku && <span className="cm-chip"><Tag size={11} /> {p.sku}</span>}
                    <span className="cm-chip">{String(p.currency || 'USD').toUpperCase()}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <p className="cm-narr cm-tabnum" style={{ fontSize: 17, color: 'var(--cm-accent)' }}>{money(p.price_cents, p.currency)}</p>
                <div className="flex items-center gap-1">
                  <button onClick={() => onEdit(p)} className="cm-btn ghost sm" title="Edit"><Pencil size={13} /> Edit</button>
                  <button onClick={() => remove(p)} disabled={deletingId === p.id} className="cm-btn danger sm" title="Delete">
                    <Trash2 size={13} className={deletingId === p.id ? 'cm-spin' : ''} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ---- PRODUCT FORM (add / edit) --------------------------------------------
function ProductForm({ product, onClose, onSaved }) {
  const isEdit = !!(product && product.id)
  const [name, setName] = useState(product?.name || '')
  const [price, setPrice] = useState(
    product?.price_cents != null ? (Number(product.price_cents) / 100).toString() : ''
  )
  const [description, setDescription] = useState(product?.description || '')
  const [sku, setSku] = useState(product?.sku || '')
  const [currency, setCurrency] = useState(String(product?.currency || 'USD').toUpperCase())
  const [active, setActive] = useState(product?.active !== false)
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (!name.trim()) { toast.error('Product name is required'); return }
    const priceNum = Number(price)
    if (!Number.isFinite(priceNum) || priceNum < 0) { toast.error('A valid price is required'); return }
    setSaving(true)
    const payload = {
      name: name.trim(),
      price: priceNum,
      description: description.trim() || null,
      sku: sku.trim() || null,
      currency: currency.trim().toUpperCase() || 'USD',
      active,
    }
    try {
      if (isEdit) await axios.patch(`${API}/products/${product.id}`, payload)
      else await axios.post(`${API}/products`, payload)
      toast.success(isEdit ? 'Product updated' : 'Product created')
      onSaved()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save product')
    } finally { setSaving(false) }
  }

  return (
    <div className="cm-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="cm-modal cm-fade">
        <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
          <h3 className="cm-title" style={{ fontSize: 18 }}>
            <Package size={18} style={{ color: 'var(--cm-accent)' }} /> {isEdit ? 'Edit product' : 'Add product'}
          </h3>
          <button onClick={onClose} className="cm-btn ghost" style={{ padding: 6 }}><X size={18} /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="cm-label">Name</label>
            <input className="cm-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Annual health screening" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="cm-label">Price</label>
              <input className="cm-input cm-tabnum" type="number" min="0" step="0.01" value={price} onChange={e => setPrice(e.target.value)} placeholder="0.00" />
            </div>
            <div>
              <label className="cm-label">Currency</label>
              <input className="cm-input" value={currency} onChange={e => setCurrency(e.target.value.toUpperCase())} placeholder="USD" maxLength={3} />
            </div>
          </div>
          <div>
            <label className="cm-label">Description</label>
            <textarea className="cm-textarea" value={description} onChange={e => setDescription(e.target.value)} placeholder="What the customer is buying…" />
          </div>
          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <label className="cm-label">SKU (optional)</label>
              <input className="cm-input" value={sku} onChange={e => setSku(e.target.value)} placeholder="SKU-001" />
            </div>
            <label className="flex items-center gap-2 cm-body" style={{ cursor: 'pointer', paddingBottom: 10 }}>
              <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--cm-accent)' }} />
              Active (sellable)
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2" style={{ marginTop: 22 }}>
          <button onClick={onClose} className="cm-btn sm">Cancel</button>
          <button onClick={save} disabled={saving} className="cm-btn primary sm">
            {saving ? <RefreshCw size={13} className="cm-spin" /> : <CheckCircle2 size={13} />}
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create product')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---- ORDERS ---------------------------------------------------------------
function OrdersSection({ orders, loading, onRefresh }) {
  const [statusFilter, setStatusFilter] = useState('all')

  const filtered = statusFilter === 'all'
    ? orders
    : orders.filter(o => String(o.status || '').toLowerCase() === statusFilter)

  const filters = ['all', 'pending', 'paid', 'fulfilled']

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap" style={{ marginBottom: 12 }}>
        <h3 className="cm-sec-h"><Receipt size={14} /> Orders</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            {filters.map(f => (
              <button key={f} onClick={() => setStatusFilter(f)}
                className="cm-chip" style={{
                  cursor: 'pointer', textTransform: 'capitalize',
                  ...(statusFilter === f ? { background: 'var(--cm-accent)', color: '#fff', borderColor: 'transparent' } : {}),
                }}>
                {f}
              </button>
            ))}
          </div>
          <button onClick={onRefresh} disabled={loading} className="cm-btn ghost sm">
            <RefreshCw size={13} className={loading ? 'cm-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {loading && orders.length === 0 && (
        <div className="cm-card cm-muted" style={{ textAlign: 'center', padding: '32px 22px' }}>Loading orders…</div>
      )}
      {!loading && filtered.length === 0 && (
        <div className="cm-card" style={{ textAlign: 'center', padding: '40px 22px' }}>
          <Receipt size={28} style={{ color: 'var(--cm-dim)', margin: '0 auto 8px' }} />
          <p className="cm-muted" style={{ fontSize: 13.5, margin: 0 }}>
            {orders.length === 0 ? 'No orders yet. They appear here as customers buy — including through the sell-in-chat agent.' : `No ${statusFilter} orders.`}
          </p>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map(o => <OrderCard key={o.id} order={o} onChange={onRefresh} />)}
      </div>
    </div>
  )
}

function OrderCard({ order, onChange }) {
  const [busy, setBusy] = useState('')
  const [link, setLink] = useState(order.payment_link || '')
  const [provider, setProvider] = useState(order.payment_provider || '')
  const st = statusOf(order.status)
  const items = Array.isArray(order.items) ? order.items : []
  const statusLc = String(order.status || '').toLowerCase()
  const settleable = statusLc === 'pending' || statusLc === 'draft'

  const createLink = async () => {
    setBusy('link')
    try {
      const r = await axios.post(`${API}/orders/${order.id}/payment-link`)
      setLink(r.data?.url || '')
      setProvider(r.data?.provider || '')
      toast.success(r.data?.provider === 'stripe' ? 'Stripe payment link ready' : 'Manual payment link ready')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not create payment link')
    } finally { setBusy('') }
  }

  const markPaid = async () => {
    if (!window.confirm(`Mark ${shortId(order.id)} as paid?`)) return
    setBusy('paid')
    try {
      await axios.post(`${API}/orders/${order.id}/mark-paid`)
      toast.success('Order marked paid')
      onChange()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not mark paid')
    } finally { setBusy('') }
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link)
      toast.success('Link copied')
    } catch { toast.error('Could not copy link') }
  }

  return (
    <div className="cm-card tight cm-fade" style={{ borderLeft: `4px solid ${st.t.c}` }}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="cm-narr cm-tabnum" style={{ fontSize: 15 }}>{shortId(order.id)}</p>
            <span className="cm-pill" style={pill(st.t)}>{st.label}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2" style={{ marginTop: 8 }}>
            {order.contact_wa_id && <span className="cm-chip"><User size={11} /> {order.contact_wa_id}</span>}
            {order.created_at && <span className="cm-dim" style={{ fontSize: 12 }}>{fmtDate(order.created_at)}</span>}
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="cm-narr cm-tabnum" style={{ fontSize: 18 }}>{money(order.total_cents, order.currency)}</p>
          <p className="cm-dim" style={{ fontSize: 12, marginTop: 2 }}>{items.length} item{items.length === 1 ? '' : 's'}</p>
        </div>
      </div>

      {/* line items */}
      {items.length > 0 && (
        <div className="cm-soft" style={{ marginTop: 12, padding: '10px 12px' }}>
          {items.map((li, i) => (
            <div key={i} className="flex items-center justify-between" style={{ padding: '3px 0' }}>
              <span className="cm-muted" style={{ fontSize: 13 }}>
                <span className="cm-tabnum" style={{ fontWeight: 800, color: 'var(--cm-ink)' }}>{li.qty || 1}×</span>{' '}
                {li.name || li.sku || 'Item'}
              </span>
              <span className="cm-muted cm-tabnum" style={{ fontSize: 13 }}>{money(li.line_total_cents ?? (li.unit_price_cents * (li.qty || 1)), order.currency)}</span>
            </div>
          ))}
        </div>
      )}

      {/* payment link surface */}
      {link && (
        <div className="cm-soft flex items-center gap-2" style={{ marginTop: 12, padding: '8px 12px' }}>
          <Link2 size={14} style={{ color: 'var(--cm-accent)', flex: 'none' }} />
          <a href={link} target="_blank" rel="noreferrer" className="cm-body" style={{ fontSize: 12.5, color: 'var(--cm-accent)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{link}</a>
          {provider && <span className="cm-chip" style={{ textTransform: 'capitalize' }}>{provider}</span>}
          <button onClick={copyLink} className="cm-btn ghost" style={{ padding: 5 }} title="Copy link"><Copy size={13} /></button>
          <a href={link} target="_blank" rel="noreferrer" className="cm-btn ghost" style={{ padding: 5 }} title="Open link"><ExternalLink size={13} /></a>
        </div>
      )}

      {/* actions */}
      {settleable && (
        <div className="flex items-center justify-end gap-2" style={{ marginTop: 14, borderTop: '1px solid var(--cm-line)', paddingTop: 12 }}>
          <button onClick={createLink} disabled={!!busy} className="cm-btn sm">
            {busy === 'link' ? <RefreshCw size={13} className="cm-spin" /> : <CreditCard size={13} />}
            {busy === 'link' ? 'Minting…' : (link ? 'New payment link' : 'Create payment link')}
          </button>
          <button onClick={markPaid} disabled={!!busy} className="cm-btn primary sm">
            {busy === 'paid' ? <RefreshCw size={13} className="cm-spin" /> : <CheckCircle2 size={13} />}
            {busy === 'paid' ? 'Saving…' : 'Mark paid'}
          </button>
        </div>
      )}
    </div>
  )
}
